<script setup lang="ts">
// Global "Ask Insiders" command palette. Opens via Cmd/Ctrl+K from anywhere
// in the agent portal, closes on Esc/backdrop click. Quick-question entry
// point that streams the assistant's text reply inline; the agent can click
// "Open in Ask →" to continue in the full /ask experience.
//
// This is a sibling surface to pages/ask.vue — it reuses the same Chat
// transport against /api/ai/chat and creates threads via the same endpoints,
// so palette conversations show up in the /ask sidebar like any other.
//
// We intentionally render ONLY text parts (no tool renderers) here — the
// palette is meant for at-a-glance answers. Anything richer belongs in /ask.

import { Chat } from '@ai-sdk/vue';
import { DefaultChatTransport } from 'ai';
import { useAskThreads } from '~/composables/useAskThreads';
import { useAskPalette } from '~/composables/useAskPalette';

const { t, locale } = useI18n();
const authStore = useAuthStore();
const { isOpenRef, close } = useAskPalette();
const { create, refresh } = useAskThreads();
const uaeGate = useUaeGate();

// Conversation id is local to the palette session — once the user navigates
// to /ask via the "Open in Ask →" link, the page picks it up via ?t=<id>.
const conversationId = ref<string | null>(null);

// Same transport shape as pages/ask.vue. Re-reads conversationId on every
// send via the function form so follow-ups inside the palette append to the
// same thread.
const chat = new Chat({
  transport: new DefaultChatTransport({
    api: '/api/ai/chat',
    headers: () => (authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
    body: () => ({ conversationId: conversationId.value ?? undefined, locale: locale.value }),
    fetch: async (input, init) => {
      const res = await fetch(input as RequestInfo, init);
      // UAE level / limit refusals: close the palette and show the next-step sheet instead.
      if (res.status === 402 || res.status === 403) {
        const body = await res
          .clone()
          .json()
          .catch(() => null);
        if (uaeGate.openFromError(body)) close();
      }
      const convId = res.headers.get('x-conversation-id');
      if (convId && conversationId.value !== convId) {
        conversationId.value = convId;
        // Refresh the sidebar (in case /ask is open in another tab or the
        // user navigates over after a palette exchange).
        refresh();
      }
      return res;
    },
  }),
});

const messages = computed(() => chat.messages);
const status = computed(() => chat.status);
const error = computed(() => chat.error);
const isBusy = computed(() => status.value === 'streaming' || status.value === 'submitted');

const input = ref('');
const inputRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLElement | null>(null);

const getTextPart = (part: unknown): string => {
  const p = part as { type?: string; text?: string };
  return typeof p?.text === 'string' ? p.text : '';
};

// Last assistant message's text — drives the streaming caret position.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isStreamingLast = (msg: any): boolean => {
  if (!isBusy.value) return false;
  const last = messages.value[messages.value.length - 1];
  return last?.id === msg.id && msg.role === 'assistant';
};

const lastMessageHasText = computed(() => {
  const last = messages.value[messages.value.length - 1];
  if (!last || last.role !== 'assistant') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (last.parts as any[]).some((p) => p.type === 'text' && typeof p.text === 'string' && p.text.length > 0);
});

const scrollToBottom = () => {
  nextTick(() => {
    const el = scrollRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
};

watch(messages, scrollToBottom, { deep: true });

const onSubmit = async () => {
  const text = input.value.trim();
  if (!text || isBusy.value) return;

  // Pre-create the thread before firing the chat so the sidebar sees the new
  // entry instantly (same pattern as pages/ask.vue).
  if (!conversationId.value) {
    const created = await create(text);
    if (created) conversationId.value = created.id;
  }

  chat.sendMessage({ text });
  input.value = '';
  scrollToBottom();
};

const openInAsk = () => {
  const target = conversationId.value ? `/ask?t=${conversationId.value}` : '/ask';
  close();
  navigateTo(target);
};

// Reset on close so the next open starts fresh unless the user explicitly
// opens the same conversation via /ask.
const reset = () => {
  conversationId.value = null;
  chat.messages = [];
  input.value = '';
};

watch(isOpenRef, (open) => {
  if (open) {
    // Focus the textarea after the modal mounts. nextTick lets v-if mount
    // the DOM first.
    nextTick(() => {
      const el = inputRef.value?.querySelector('textarea') as HTMLTextAreaElement | null;
      el?.focus();
    });
  } else {
    // Slight delay so the close animation can play out before the messages
    // disappear visually.
    setTimeout(reset, 200);
  }
});

// Backdrop click closes; clicks inside the card stop propagation in the
// template so they don't bubble up to the backdrop.
const onBackdropClick = () => {
  close();
};
</script>

<template>
  <Teleport to="body">
    <Transition name="ask-palette">
      <div
        v-if="isOpenRef"
        class="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black-100/40 p-4 backdrop-blur-[2px]"
        role="dialog"
        aria-modal="true"
        :aria-label="t('ask.commandPalette.ariaLabel')"
        @click="onBackdropClick"
      >
        <!-- my-auto centres the card vertically; when an answer makes it taller than the
             screen it falls back to the top and the overlay scrolls instead of clipping. -->
        <div
          class="ask-palette-card my-auto w-full max-w-[720px] overflow-hidden rounded-2xl border border-black-10 bg-white shadow-[0_24px_64px_-24px_rgba(13,15,21,0.45)]"
          @click.stop
        >
          <!-- Header strip -->
          <div class="flex items-center justify-between gap-3 border-b border-black-10 px-5 py-3">
            <div class="flex items-center gap-2.5">
              <span
                class="grid size-[22px] flex-shrink-0 place-items-center rounded-md bg-black-100 font-mono text-[10px] font-bold tracking-[-0.02em] text-gold-primary-100"
              >
                {{ t('ask.common.aiInitials') }}
              </span>
              <span class="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-black-60">
                {{ t('ask.commandPalette.eyebrow') }}
              </span>
            </div>
            <div class="flex items-center gap-3">
              <button
                v-if="messages.length"
                type="button"
                class="font-mono text-[10.5px] uppercase tracking-[0.12em] text-black-60 transition-colors hover:text-black-100"
                @click="openInAsk"
              >
                {{ t('ask.commandPalette.openInAsk') }}
                <span
                  aria-hidden="true"
                  class="inline-block rtl:-scale-x-100"
                  >&rarr;</span
                >
              </button>
              <kbd class="hidden font-mono text-[10px] uppercase tracking-[0.1em] text-black-40 tablet:inline">
                Esc
              </kbd>
            </div>
          </div>

          <!-- Composer -->
          <div
            ref="inputRef"
            class="px-5 py-4"
          >
            <AskConsoleInput
              v-model="input"
              :disabled="isBusy"
              size="lg"
              :placeholder="t('ask.commandPalette.inputPlaceholder')"
              @submit="onSubmit"
            />
          </div>

          <!-- Streaming reply area — only mounted once there's something to
               show, so the empty palette stays small. -->
          <div
            v-if="messages.length || isBusy"
            ref="scrollRef"
            class="max-h-[420px] overflow-y-auto border-t border-black-10 px-5 py-4"
          >
            <article
              v-for="msg in messages"
              :key="msg.id"
              class="mb-4"
            >
              <div
                v-if="msg.role === 'user'"
                class="flex items-start gap-3"
              >
                <span
                  class="w-[40px] flex-shrink-0 pt-1 font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-black-40"
                >
                  {{ t('ask.commandPalette.youLabel') }}
                </span>
                <div class="flex-1 text-[14px] leading-[1.5] font-medium text-black-100">
                  <p
                    v-for="(part, i) in msg.parts"
                    :key="`${msg.id}-${i}`"
                    class="whitespace-pre-wrap"
                  >
                    {{ getTextPart(part) }}
                  </p>
                </div>
              </div>

              <div
                v-else-if="msg.role === 'assistant'"
                class="flex items-start gap-3"
              >
                <span
                  class="w-[40px] flex-shrink-0 pt-1 font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-gold-primary-120"
                >
                  {{ t('ask.common.aiInitials') }}
                </span>
                <div class="min-w-0 flex-1">
                  <template
                    v-for="(part, i) in msg.parts"
                    :key="`${msg.id}-${i}`"
                  >
                    <AskMarkdownContent
                      v-if="part.type === 'text'"
                      :text="getTextPart(part)"
                    />
                  </template>
                  <span
                    v-if="isStreamingLast(msg)"
                    class="ask-palette-caret ms-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] bg-gold-primary-120 align-baseline"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </article>

            <!-- Pre-token loading row — matches /ask styling. -->
            <div
              v-if="isBusy && !lastMessageHasText"
              class="flex items-start gap-3"
            >
              <span
                class="w-[40px] flex-shrink-0 pt-1 font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-gold-primary-120"
              >
                {{ t('ask.common.aiInitials') }}
              </span>
              <span class="ask-palette-dots inline-flex items-center gap-1 pt-1.5">
                <span class="size-1.5 rounded-full bg-gold-primary-120" />
                <span class="size-1.5 rounded-full bg-gold-primary-120" />
                <span class="size-1.5 rounded-full bg-gold-primary-120" />
              </span>
            </div>

            <div
              v-if="error"
              class="ms-[52px] mt-2 rounded-md border border-[#ef4444]/30 bg-[#ef4444]/10 p-2.5 text-[12.5px] text-[#b91c1c]"
            >
              {{ error.message || t('ask.common.somethingWentWrong') }}
            </div>
          </div>

          <!-- Empty-state hint row -->
          <div
            v-else
            class="flex items-center justify-between gap-3 border-t border-black-10 px-5 py-3"
          >
            <i18n-t
              keypath="ask.commandPalette.pressEnterToSend"
              tag="span"
              class="font-mono text-[10.5px] uppercase tracking-[0.14em] text-black-40"
            >
              <template #enter><span class="text-black-60">↵</span></template>
            </i18n-t>
            <NuxtLink
              to="/ask"
              class="font-mono text-[10.5px] uppercase tracking-[0.12em] text-black-60 transition-colors hover:text-black-100"
              @click="close"
            >
              {{ t('ask.commandPalette.fullExperience') }}
              <span
                aria-hidden="true"
                class="inline-block rtl:-scale-x-100"
                >&rarr;</span
              >
            </NuxtLink>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
// Streaming-text caret — matches /ask.
.ask-palette-caret {
  animation: ask-palette-caret-pulse 1s ease-in-out infinite;
}

@keyframes ask-palette-caret-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
}

// Bouncing dots — matches /ask.
.ask-palette-dots span {
  animation: ask-palette-dot-bounce 1.1s ease-in-out infinite both;
}
.ask-palette-dots span:nth-child(2) {
  animation-delay: 0.16s;
}
.ask-palette-dots span:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes ask-palette-dot-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

// Modal enter/leave — 180ms fade + slight upward slide on the card.
.ask-palette-enter-active,
.ask-palette-leave-active {
  transition: opacity 0.18s ease;
}
.ask-palette-enter-from,
.ask-palette-leave-to {
  opacity: 0;
}
.ask-palette-enter-active .ask-palette-card,
.ask-palette-leave-active .ask-palette-card {
  transition:
    transform 0.18s cubic-bezier(0.2, 0.6, 0.2, 1),
    opacity 0.18s ease;
}
.ask-palette-enter-from .ask-palette-card,
.ask-palette-leave-to .ask-palette-card {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
